
import { 
  Activity, 
  DollarSign, 
  Snowflake, 
  AlertTriangle, 
  Settings, 
  Map, 
  Wind, 
  TrendingUp, 
  Ghost, 
  Octagon,
  Globe,
  Wrench,
  BarChart,
  Sun,
  Cog,
  Package,
  ZapOff,
  Flame,
  Receipt,
  Award
} from 'lucide-react';
import React from 'react';

export type KpiItemIcon = React.ReactNode;

export type KpiDataItem = {
  id: string;
  title: string;
  value: string;
  unit: string;
  trend: string;
  isPositive: boolean | null;
  icon: KpiItemIcon;
  colorClass: string;
  isVisible: boolean;
};

export const kpiData: KpiDataItem[] = [
  {
    id: 'energy-consumption',
    title: "Total Energy Consumption",
    value: "8,420",
    unit: "kWh",
    trend: "+3.2% vs yesterday",
    isPositive: false,
    icon: <Activity className="h-6 w-6 text-energy-blue" />,
    colorClass: "bg-blue-50 dark:bg-blue-900/20",
    isVisible: true
  },
  {
    id: 'energy-cost',
    title: "Total Energy Cost",
    value: "$1,862",
    unit: "",
    trend: "-5.4% forecasted",
    isPositive: true,
    icon: <DollarSign className="h-6 w-6 text-green-600" />,
    colorClass: "bg-green-50 dark:bg-green-900/20",
    isVisible: true
  },
  {
    id: 'chiller-efficiency',
    title: "Chiller Efficiency",
    value: "3.8",
    unit: "COP",
    trend: "-0.2",
    isPositive: false,
    icon: <Snowflake className="h-6 w-6 text-cyan-600" />,
    colorClass: "bg-cyan-50 dark:bg-cyan-900/20",
    isVisible: true
  },
  {
    id: 'peak-load',
    title: "Peak Load",
    value: "412",
    unit: "kW at 3:10 PM",
    trend: "Highest in last 7 days",
    isPositive: false,
    icon: <AlertTriangle className="h-6 w-6 text-amber-600" />,
    colorClass: "bg-amber-50 dark:bg-amber-900/20",
    isVisible: true
  },
  {
    id: 'power-factor',
    title: "Power Factor",
    value: "0.92",
    unit: "",
    trend: "Acceptable",
    isPositive: true,
    icon: <Settings className="h-6 w-6 text-gray-600" />,
    colorClass: "bg-gray-50 dark:bg-gray-900/20",
    isVisible: true
  },
  {
    id: 'zone-consumption',
    title: "Zone-wise Consumption",
    value: "Tap to expand heatmap",
    unit: "",
    trend: "",
    isPositive: null,
    icon: <Map className="h-6 w-6 text-indigo-600" />,
    colorClass: "bg-indigo-50 dark:bg-indigo-900/20",
    isVisible: true
  },
  {
    id: 'hvac-runtime',
    title: "HVAC Runtime",
    value: "6.3",
    unit: "hrs today",
    trend: "Efficiency flag if >8 hrs",
    isPositive: true,
    icon: <Wind className="h-6 w-6 text-teal-600" />,
    colorClass: "bg-teal-50 dark:bg-teal-900/20",
    isVisible: true
  },
  {
    id: 'predicted-energy',
    title: "Predicted Energy Cost",
    value: "$5,060",
    unit: "next 30 days",
    trend: "12% rise expected",
    isPositive: false,
    icon: <TrendingUp className="h-6 w-6 text-rose-600" />,
    colorClass: "bg-rose-50 dark:bg-rose-900/20",
    isVisible: true
  },
  {
    id: 'idle-load',
    title: "Idle Load",
    value: "39",
    unit: "kWh overnight",
    trend: "Alert if >25 kWh",
    isPositive: false,
    icon: <Ghost className="h-6 w-6 text-violet-600" />,
    colorClass: "bg-violet-50 dark:bg-violet-900/20",
    isVisible: true
  },
  {
    id: 'system-anomalies',
    title: "System Anomalies",
    value: "2",
    unit: "unresolved",
    trend: "Tap to investigate",
    isPositive: false,
    icon: <Octagon className="h-6 w-6 text-red-600" />,
    colorClass: "bg-red-50 dark:bg-red-900/20",
    isVisible: true
  }
];

export const advancedKpiData: KpiDataItem[] = [
  {
    id: 'carbon-emissions',
    title: "Carbon Emissions",
    value: "412",
    unit: "kgCO₂e",
    trend: "-6.5% vs last month",
    isPositive: true,
    icon: <Globe className="h-6 w-6 text-green-600" />,
    colorClass: "bg-green-50 dark:bg-green-900/20",
    isVisible: true
  },
  {
    id: 'equipment-health-score',
    title: "Equipment Health Score",
    value: "84",
    unit: "/ 100",
    trend: "Good",
    isPositive: true,
    icon: <Wrench className="h-6 w-6 text-blue-600" />,
    colorClass: "bg-blue-50 dark:bg-blue-900/20",
    isVisible: true
  },
  {
    id: 'load-profile-deviation',
    title: "Load Profile Deviation",
    value: "+8%",
    unit: "above baseline",
    trend: "Alert if deviation >10%",
    isPositive: false,
    icon: <BarChart className="h-6 w-6 text-amber-600" />,
    colorClass: "bg-amber-50 dark:bg-amber-900/20",
    isVisible: true
  },
  {
    id: 'solar-power-contribution',
    title: "Solar Power Contribution",
    value: "1,120",
    unit: "kWh (13.3%)",
    trend: "Up if solar generation increases",
    isPositive: true,
    icon: <Sun className="h-6 w-6 text-yellow-600" />,
    colorClass: "bg-yellow-50 dark:bg-yellow-900/20",
    isVisible: true
  },
  {
    id: 'energy-intensity',
    title: "Energy Intensity",
    value: "2.4",
    unit: "kWh/unit",
    trend: "Ideal ≤ 2.0",
    isPositive: false,
    icon: <Cog className="h-6 w-6 text-gray-600" />,
    colorClass: "bg-gray-50 dark:bg-gray-900/20",
    isVisible: true
  },
  {
    id: 'maintenance-due-soon',
    title: "Maintenance Due Soon",
    value: "3",
    unit: "assets",
    trend: "List next service dates",
    isPositive: null,
    icon: <Package className="h-6 w-6 text-purple-600" />,
    colorClass: "bg-purple-50 dark:bg-purple-900/20",
    isVisible: true
  },
  {
    id: 'unbalanced-load-detection',
    title: "Unbalanced Load Detection",
    value: "2",
    unit: "panels flagged",
    trend: "Phase A deviation >15%",
    isPositive: false,
    icon: <ZapOff className="h-6 w-6 text-orange-600" />,
    colorClass: "bg-orange-50 dark:bg-orange-900/20",
    isVisible: true
  },
  {
    id: 'generator-runtime',
    title: "Generator Runtime",
    value: "2.8",
    unit: "hrs today",
    trend: "Fuel Cost: $132",
    isPositive: null,
    icon: <Flame className="h-6 w-6 text-red-600" />,
    colorClass: "bg-red-50 dark:bg-red-900/20",
    isVisible: true
  },
  {
    id: 'utility-bill-reconciliation',
    title: "Utility Bill Reconciliation",
    value: "1",
    unit: "mismatch",
    trend: "Alert if unresolved",
    isPositive: false,
    icon: <Receipt className="h-6 w-6 text-indigo-600" />,
    colorClass: "bg-indigo-50 dark:bg-indigo-900/20",
    isVisible: true
  },
  {
    id: 'benchmark-vs-similar-plants',
    title: "Benchmark vs. Similar Plants",
    value: "Top 25%",
    unit: "efficiency",
    trend: "Based on anonymized sector comparison",
    isPositive: true,
    icon: <Award className="h-6 w-6 text-teal-600" />,
    colorClass: "bg-teal-50 dark:bg-teal-900/20",
    isVisible: true
  }
];
